// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class GenericDocsService {

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService
  ) { }
  
  /**
   * Create a new generic doc
   *
   * @since 1.0.0
   *
   * @param data GenreicDoc data
   * @returns Observable<any>
   */
   create(data: any): Observable<any> {
    return this.apiService.apiCall("POST", "generic-docs", data);
  }

  /**
   * Get Groundings list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(companyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `generic-docs/${companyId}`, params);
  }
  
  /**
   * Get the generic doc
   *
   * @since 1.0.0
   *
   * @param docId GenreicDoc Id
   * @returns Observable<any>
   */
  get(companyId: string, docId: string): Observable<any> {
    return this.apiService.apiCall("GET", `generic-docs/${companyId}/${docId}`);
  }
  
  /**
   * Get the generic doc
   *
   * @since 1.0.0
   *
   * @param docId GenreicDoc Id
   * @returns Observable<any>
   */
  getDocByView(docId: string): Observable<any> {
    return this.apiService.apiCall("GET", `generic-docs/doc-by-view/${docId}`);
  }

  /**
   * Update a generic doc
   *
   * @since 1.0.0
   *
   * @param data GenreicDoc data
   * @returns Observable<any>
   */
  update(companyId: string, docId: string, data: any): Observable<any> {
    return this.apiService.apiCall("PATCH", `generic-docs/${companyId}/${docId}`, data);
  }

  /**
   * Delete a generic doc
   *
   * @since 1.0.0
   *
   * @param data GenreicDoc data
   * @returns Observable<any>
   */
   delete(companyId: string, docId: string): Observable<any> {
    return this.apiService.apiCall("DELETE", `generic-docs/${companyId}/${docId}`);
  }

  /**
   * This gets the occupied space of generic documents, in the measure of MB or GB
   *
   * @since 1.0.0
   */
  getOccupiedSpace(companyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `generic-docs/occupied-space/${companyId}`);
  }

  /**
   * Get an exportation file of all generic docs
   *
   * @since 1.0.0
   */
  getExport(companyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `generic-docs/export/${companyId}`, {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

}
